import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { useQuery } from '@apollo/client';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  GetProductDetailAdsQuery,
  GetProductDetailAdsQueryVariables,
} from '@notino/shared/definitions/types';

import { useProductDetailContext } from '../../ProductDetailContext';
import { MAX_SPONSORED_PRODUCTS } from '../SponsoredAdsProducts/constants';
import { isDiorBrand } from '../utils';

import getProductDetailAdsQuery from './queries/productDetailAds.graphql';
import {
  flattenCarouselProducts,
  getRenderedOccurrences,
  normalizeProvider,
} from './tracking';

/**
 * Fetches the product-detail ads carousel and derives the ordered, capped tracking
 * occurrences plus the ids ProductsSection renders.
 *
 * The request fires as soon as the hook mounts — i.e. right after hydration, since the
 * query is client-only — because the ad provider wants to be called on page load rather
 * than when the section is scrolled to. `inView` therefore only gates *rendering*.
 */
export const useProductDetailAds = () => {
  const [gateRef, inView] = useInView({ triggerOnce: true });
  const { product } = useProductDetailContext();

  const { data, loading } = useQuery<
    GetProductDetailAdsQuery,
    GetProductDetailAdsQueryVariables
  >(getProductDetailAdsQuery, {
    variables: {
      input: {
        masterId: Number(product.catalogMasterId),
        products: product.variants.map((variant) => ({
          id: Number(variant.catalogId),
          price: variant.price.value ?? 0,
          isAvailable: variant.stockAvailability.code !== IStock.outOfStock,
        })),
        excludeFromAds: isDiorBrand(product.brand?.id),
      },
    },
    ssr: false,
  });

  const carousel = data?.ProductDetailAds?.productCarousel;
  const provider = normalizeProvider(carousel?.provider);

  const occurrences = React.useMemo(
    () => flattenCarouselProducts(carousel),
    [carousel]
  );
  // Only the first MAX_SPONSORED_PRODUCTS occurrences are actually rendered.
  const cappedOccurrences = React.useMemo(
    () => occurrences.slice(0, MAX_SPONSORED_PRODUCTS),
    [occurrences]
  );
  const sponsoredProductIds = React.useMemo(
    () => cappedOccurrences.map((o) => o.productId),
    [cappedOccurrences]
  );
  // Exactly the products reported as rendered: anything unresolvable or out of stock is
  // left out of the carousel and reported as unrendered instead.
  const sponsoredProducts = React.useMemo(
    () => getRenderedOccurrences(cappedOccurrences).map((o) => o.vpProduct),
    [cappedOccurrences]
  );

  return {
    gateRef,
    inView,
    loading,
    carousel,
    provider,
    occurrences,
    cappedOccurrences,
    sponsoredProductIds,
    sponsoredProducts,
  };
};
